import { Injectable } from "@angular/core";
import { AbstractControl, ValidatorFn, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import Decimal from "decimal.js";
import { Observable } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { RangeFeature } from "../model/catalog/catalog.model";
import { FieldName, FieldValidationError, ValidationError } from "../model/validator/validator";
import { CatalogService } from "../utils/catalog.service";
import { i18nService } from "./i18n.service";

@Injectable()
export class ValidatorService {

    constructor(private i18nService: i18nService,
                private translateService: TranslateService,
                private catalogService: CatalogService) {}

    getErrorText(error: ValidationError): Observable<string> {
        if(error instanceof FieldValidationError) {
          return this.getFieldName(error.field).pipe(
            mergeMap(fieldName => this.translateService.get(error.messageKey, {field: fieldName, ...error.params}))
          );
        } else {
          return this.translateService.get(error.messageKey, error.params);
        }
    }

    private getFieldName(fieldName: FieldName): Observable<string> {
        if(!fieldName) {
          return this.translateService.get("common.validation.defaultFieldName");
        } else if(typeof fieldName === "string") {
          return this.catalogService.getFeatureTranslation(fieldName);
        } else {
          return fieldName;
        }
    }

    requiredFieldValidator(fieldName: FieldName = null): ValidatorFn { return control => {
      if ((control.touched || control.dirty) && control.value === "") {
        return { "required" : new FieldValidationError("common.validation.required", fieldName) }
      } else {
        return null;
      }
    }}

    numericValidator(fieldName: FieldName = null): ValidatorFn { return control => {
      if (control.value && control.value !== '' && !(/^-?\d+(?:[,.]\d+)?$/.test(control.value))) {
        return { "numeric" : new FieldValidationError("common.validation.numeric", fieldName) }
      } else {
        return null;
      }
    }}

    rangeValidator(feature: RangeFeature, dependency?: number): ValidatorFn {
        return control => {
            if (control.value) {
              if(this.i18nService.isNumber(control.value)) {
                let val = new Decimal(this.i18nService.parseNumber(control.value));

                // hidden ranges for radius
                let rangeMin = feature.rangeMin;
                let rangeMax = dependency? dependency : feature.rangeMax;
                if(feature.name.includes("Radius")){
                    rangeMin = Math.round((rangeMin - 5 + Number.EPSILON) * 100) / 100;
                    rangeMax = rangeMax + 5;
                }

                if(val.lessThan(rangeMin) || val.greaterThan(rangeMax) || (feature.rangeStep && !val.sub(rangeMin).div(feature.rangeStep).isInteger())) {
                  return {
                    "range": new FieldValidationError("common.validation.range", feature.name,
                      {
                        rangeMax: this.i18nService.formatDecimal(dependency ? dependency : feature.rangeMax),
                        rangeMin: this.i18nService.formatDecimal(feature.rangeMin),
                        rangeStep: this.i18nService.formatDecimal(feature.rangeStep)
                      })
                  };
                }
              }
            }
            return null;
        }
    }
}
